import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles, Zap, Check, Bell } from 'lucide-react';
import { 
  AnimatedButton, 
  HoverCard3D, 
  GlowCard, 
  FloatingInput,
  AnimatedCounter,
  AnimatedProgress,
  NotificationBadge,
  AnimatedTooltip,
  SuccessCheckmark
} from '../components/ui/micro-interactions';
import {
  Skeleton,
  StatsSkeleton,
  ChartSkeleton,
  VehicleCardSkeleton,
  LeadCardSkeleton,
  TableSkeleton,
  InlineLoader,
  PulseDotLoader
} from '../components/ui/skeletons';

export default function ComponentsDemo() {
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  const handleButtonClick = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                  Component Library
                </h1>
                <p className="text-sm text-gray-500">Micro-interactions & Loading States</p>
              </div>
            </div>
            <img src="/wakzy-logo.png" alt="Wakzy" className="h-10" />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Animated Buttons */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Zap className="w-6 h-6 text-blue-500" />
            Animated Buttons
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <AnimatedButton variant="primary" onClick={handleButtonClick} loading={loading}>
              {loading ? 'Processing...' : 'Primary Button'}
            </AnimatedButton>
            <AnimatedButton variant="secondary">
              Secondary
            </AnimatedButton>
            <AnimatedButton variant="success">
              Success
            </AnimatedButton>
            <AnimatedButton variant="warning">
              Warning
            </AnimatedButton>
          </div>
          {showSuccess && (
            <div className="mt-6 flex justify-center animate-scale-in">
              <SuccessCheckmark size={64} />
            </div>
          )}
        </section>

        {/* Hover Cards */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-purple-500" />
            3D Hover Cards
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {['blue', 'purple', 'green'].map((color, i) => (
              <HoverCard3D key={i}>
                <GlowCard glowColor={color} className="bg-white rounded-2xl p-6 border border-gray-200 hover-lift">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-r from-${color}-500 to-${color === 'blue' ? 'cyan' : color === 'purple' ? 'pink' : 'emerald'}-500 flex items-center justify-center mb-4`}>
                    <Sparkles className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Feature Card</h3>
                  <p className="text-gray-600">Hover over me to see the 3D tilt effect and glow!</p>
                </GlowCard>
              </HoverCard3D>
            ))}
          </div>
        </section>

        {/* Floating Inputs */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Floating Label Inputs</h2>
          <div className="max-w-md space-y-4">
            <FloatingInput 
              label="Email Address" 
              type="email"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
            />
            <FloatingInput 
              label="Password" 
              type="password"
              value=""
              onChange={() => {}}
            />
            <FloatingInput 
              label="With Error" 
              type="text"
              value="invalid"
              onChange={() => {}}
              error="This field has an error"
            />
          </div>
        </section>

        {/* Animated Counters & Progress */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Counters & Progress</h2>
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-gray-200 text-center">
              <div className="text-4xl font-bold text-blue-600">
                <AnimatedCounter value={1250} prefix="$" duration={1500} />
              </div>
              <p className="text-gray-500 mt-2">Revenue</p>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-gray-200 text-center">
              <div className="text-4xl font-bold text-green-600">
                <AnimatedCounter value={98} suffix="%" duration={1000} />
              </div>
              <p className="text-gray-500 mt-2">Success Rate</p>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-gray-200 text-center">
              <div className="text-4xl font-bold text-purple-600">
                <AnimatedCounter value={500} suffix="+" duration={1200} />
              </div>
              <p className="text-gray-500 mt-2">Dealers</p>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-gray-200 text-center relative">
              <NotificationBadge count={5} />
              <Bell className="w-10 h-10 text-gray-400 mx-auto" />
              <p className="text-gray-500 mt-2">Notifications</p>
            </div>
          </div>
          
          <div className="max-w-xl space-y-6">
            <div>
              <p className="text-sm text-gray-600 mb-2">Sales Progress (Blue)</p>
              <AnimatedProgress value={75} max={100} color="blue" />
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-2">Goals Progress (Purple)</p>
              <AnimatedProgress value={45} max={100} color="purple" />
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-2">Revenue Progress (Green)</p>
              <AnimatedProgress value={90} max={100} color="green" />
            </div>
          </div>
        </section>

        {/* Tooltips */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Animated Tooltips</h2>
          <div className="flex gap-8 flex-wrap">
            <AnimatedTooltip content="Tooltip on top" position="top">
              <button className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors">
                Hover (Top)
              </button>
            </AnimatedTooltip>
            <AnimatedTooltip content="Tooltip on bottom" position="bottom">
              <button className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors">
                Hover (Bottom)
              </button>
            </AnimatedTooltip>
            <AnimatedTooltip content="Tooltip on left" position="left">
              <button className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors">
                Hover (Left)
              </button>
            </AnimatedTooltip>
            <AnimatedTooltip content="Tooltip on right" position="right">
              <button className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors">
                Hover (Right)
              </button>
            </AnimatedTooltip>
          </div>
        </section>

        {/* Loading States */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Loading Spinners</h2>
          <div className="flex items-center gap-8 flex-wrap">
            <div className="flex flex-col items-center gap-2">
              <InlineLoader size="sm" color="blue" />
              <span className="text-sm text-gray-500">Small</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <InlineLoader size="md" color="purple" />
              <span className="text-sm text-gray-500">Medium</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <InlineLoader size="lg" color="green" />
              <span className="text-sm text-gray-500">Large</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <PulseDotLoader />
              <span className="text-sm text-gray-500">Pulse Dots</span>
            </div>
          </div>
        </section>

        {/* Skeleton Loaders */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Skeleton Loaders</h2>
          
          <h3 className="text-lg font-semibold text-gray-700 mb-4">Stats Cards</h3>
          <StatsSkeleton />
          
          <h3 className="text-lg font-semibold text-gray-700 mb-4 mt-8">Charts</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <ChartSkeleton />
            <ChartSkeleton />
          </div>
          
          <h3 className="text-lg font-semibold text-gray-700 mb-4 mt-8">Vehicle Cards</h3>
          <div className="grid md:grid-cols-3 gap-6">
            <VehicleCardSkeleton />
            <VehicleCardSkeleton />
            <VehicleCardSkeleton />
          </div>
          
          <h3 className="text-lg font-semibold text-gray-700 mb-4 mt-8">Lead Cards</h3>
          <div className="grid md:grid-cols-3 gap-6">
            <LeadCardSkeleton />
            <LeadCardSkeleton />
            <LeadCardSkeleton />
          </div>
          
          <h3 className="text-lg font-semibold text-gray-700 mb-4 mt-8">Table</h3>
          <TableSkeleton rows={3} />
        </section>

        {/* Basic Skeletons */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Basic Skeleton Variants</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <p className="text-sm text-gray-600 mb-2">Text</p>
              <Skeleton variant="text" className="w-full" />
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-2">Title</p>
              <Skeleton variant="title" className="w-3/4" />
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-2">Avatar</p>
              <Skeleton variant="avatar" />
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-2">Button</p>
              <Skeleton variant="button" className="w-32" />
            </div>
          </div>
        </section>

        {/* CSS Animation Classes */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">CSS Animation Classes</h2>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-white rounded-2xl p-6 border border-gray-200 animate-fade-in">
              <p className="font-medium text-gray-900">animate-fade-in</p>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-gray-200 animate-slide-up">
              <p className="font-medium text-gray-900">animate-slide-up</p>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-gray-200 animate-bounce-in">
              <p className="font-medium text-gray-900">animate-bounce-in</p>
            </div>
            <div className="bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl p-6 animate-glow-pulse">
              <p className="font-medium text-white">animate-glow-pulse</p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
